import {Box} from "@mui/material";
import React, {ComponentPropsWithoutRef, useCallback, useEffect, useState} from "react";
import axiosInstant from "../../../../api";
import {
    getBrandByVideoId,
    getBrandProject,
    getVideoUGC,
    getVideoFileV2,
} from "../../../../api/endpoints";
import {IVideo} from "../hooks/useFetchVideos";
import {Header} from "./Header";
import type {PanelCounts} from "./Header";
import {ThumbnailsPanel} from "./ThumbnailsPanel";
import {CaptionsPanel} from "./CaptionsPanel";
import {StoriesPanel} from "./StoriesPanel";
import {PerformancePanel} from "./performance/PerformancePanel";
import {PageScorePanel} from "./performance/PageScorePanel";
import {MainContainer} from "../../../../components/ContentWrapper";
import {DetailSkeleton, DetailNotFound, NestedLoadingBar} from "./PageStates";
import {NestedModuleBar} from "./NestedModuleBar";

const extractIconikAssetId = (key: string): string | null => {
    if (!key) return null;
    // Extract filename from the full path
    const filename = key.substring(key.lastIndexOf("/") + 1);
    // Remove extension
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
    // UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const uuidRegex =
        /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
    const match = nameWithoutExt.match(uuidRegex);
    return match ? match[1] : null;
};

const extractIconikStorageId = (key: string): string | null => {
    if (!key) return null;
    const firstSlashIndex = key.indexOf("/");
    if (firstSlashIndex === -1) return key;
    return key.substring(0, firstSlashIndex);
};

interface SelectedVideoCardProps extends ComponentPropsWithoutRef<"div"> {
    videoId: number;
    type: string;
    containerClassName?: string;
    refetch: () => void;
    onClose?: () => void;
    /**
     * Rendered INSIDE another page (the expert profile, product detail, story
     * detail) rather than replacing the page body. That host already owns a
     * title and a breadcrumb, so this view suppresses both and collapses its
     * identity into one inline 44px bar inside a bordered module shell (§04).
     */
    nested?: boolean;
    /** The video's aspect ratio when the host already knows it, so the loading
     *  skeleton reserves the right plate instead of always landscape. */
    aspectHint?: string;
}

export function SelectedVideoV2(props: SelectedVideoCardProps) {
    const {videoId, type, onClose, refetch, containerClassName, nested = false, aspectHint} = props;
    const [video, setVideo] = useState<IVideo>();

    const [brand, setBrand] = useState<any>({});
    // Replaces the silent onClose(). 404 / 403 / anything else each get a page.
    const [pageError, setPageError] = useState<null | "notfound" | "forbidden" | "error">(null);
    const [pageAttempt, setPageAttempt] = useState(0);
    // Bumped by any kebab action. "Add to story" / "Remove from story" change
    // story membership, but StoriesPanel owns its own fetch and is keyed on a
    // video id that did not change — without this the panel and its jump-rail
    // count stayed stale until a full reload.
    const [contentNonce, setContentNonce] = useState(0);
    // Bumped when the settings modal closes. Its Product Pages tab is where a
    // page gets assigned to this video, and the modal only reports General-tab
    // saves — so the Page score panel refreshes on close instead.
    const [pagesNonce, setPagesNonce] = useState(0);
    const retryPage = useCallback(() => setPageAttempt((n) => n + 1), []);
    // Panel counts ride up so the jump rail can show them. Each setter is
    // identity-stable and bails on an unchanged value, so a panel reporting the
    // same count cannot loop.
    const [counts, setCounts] = useState<PanelCounts>({});
    // The video's project, loaded once for every part of the page that needs
    // it: the breadcrumb, and each download control's gate — the video payload
    // restricts its project to `attributes: ['id']`, and a file owned through
    // its project carries its brands' pre-approval setting only there.
    const videoProjectId = video ? video.projectId ?? (video as any).project?.id : undefined;
    const [videoProject, setVideoProject] = useState<any>(null);
    useEffect(() => {
        if (!videoProjectId) {
            setVideoProject(null);
            return undefined;
        }
        let active = true;
        axiosInstant
            .get(getBrandProject(Number(videoProjectId)))
            .then((res) => active && setVideoProject(res.data))
            .catch(() => active && setVideoProject(null));
        return () => {
            active = false;
        };
    }, [videoProjectId]);
    const onStories = useCallback(
        (n: number) => setCounts((c) => (c.stories === n ? c : {...c, stories: n})),
        [],
    );
    const onThumbnails = useCallback(
        (n: number) => setCounts((c) => (c.thumbnails === n ? c : {...c, thumbnails: n})),
        [],
    );
    const onTranscripts = useCallback(
        (n: number) => setCounts((c) => (c.transcripts === n ? c : {...c, transcripts: n})),
        [],
    );
    // No number at 0 or 1 (§10.3): "Page score 0" reads as a score of zero, the
    // one figure this panel must never accidentally assert.
    const onPages = useCallback(
        (n: number) =>
            setCounts((c) => {
                const v = n >= 2 ? n : undefined;
                return c.pages === v ? c : {...c, pages: v};
            }),
        [],
    );

    const classifyError = (err: any) => {
        const code = err?.response?.status;
        if (code === 404) return "notfound" as const;
        if (code === 403 || code === 401) return "forbidden" as const;
        return "error" as const;
    };

    const fetchBrand = async () => {
        const endpoint = getBrandByVideoId(String(videoId));
        try {
            const response = await axiosInstant.get(endpoint);
            const {data} = response;
            if (data) {
                setBrand(data);
            }
        } catch (err) {
            setPageError(classifyError(err));
        }
    };

    const fetchVideo = async () => {
        let endpoint: any;

        switch (type) {
            case "review":
                endpoint = getVideoUGC(brand.id, videoId);
                break;
            case "file":
            default:
                endpoint = getVideoFileV2(videoId);
                break;
        }

        try {
            const response = await axiosInstant.get(endpoint);
            const {data} = response;
            if (data) {
                // The embeds fetch that used to live here was the SECOND call
                // to `getVideoEmbeds` on this page: its only consumer was the
                // old Analytics section's `embeds` prop, which that component
                // never actually read (it used `video.embeds` from its own
                // payload). StoriesPanel makes the one real call.
                setVideo(data);
            }
        } catch (err) {
            setPageError(classifyError(err));
        }
    };

    useEffect(() => {
        setPageError(null);
        fetchBrand();
    }, [videoId, pageAttempt]);

    useEffect(() => {
        if (videoId && brand.id) fetchVideo();
    }, [brand, videoId]);

    const updateVideoPage = () => {
        refetch();
        fetchVideo();
        setContentNonce((n) => n + 1);
    };

    /** In a nested host every state lives inside the module shell, so the host
     *  page keeps its own chrome instead of being replaced. */
    const shell = (children: React.ReactNode) => {
        if (!nested) return children;
        return (
            <Box
                className="border border-border-token rounded-xl overflow-hidden bg-surface"
                sx={{boxShadow: "var(--sh-1)"}}
            >
                {children}
            </Box>
        );
    };

    if (pageError) {
        return (
            <MainContainer containerClassName={containerClassName}>
                {shell(
                    <DetailNotFound
                        kind={pageError}
                        onBack={onClose}
                        onRetry={retryPage}
                        videoId={videoId}
                    />,
                )}
            </MainContainer>
        );
    }

    if (!video) {
        return (
            <MainContainer containerClassName={containerClassName}>
                {shell(
                    nested ? (
                        <>
                            <NestedLoadingBar onBack={onClose} />
                            <Box className="p-4 bg-page">
                                <DetailSkeleton aspectHint={aspectHint} nested />
                            </Box>
                        </>
                    ) : (
                        <DetailSkeleton aspectHint={aspectHint} onBack={onClose} />
                    ),
                )}
            </MainContainer>
        );
    }

    const menuProps = {
        brand,
        video,
        refetch: updateVideoPage,
        onSettingsClosed: () => setPagesNonce((n) => n + 1),
        // Delete removes the record: reload the host list and close.
        // Never updateVideoPage — its fetchVideo would 404 the id.
        onDeleted: () => {
            refetch();
            onClose?.();
        },
    };

    const body = (
        <>
            <Header {...props} menuProps={menuProps} video={video} counts={counts} nested={nested} project={videoProject} />

            {/* Page score first: it scores something on almost every video,
                where Performance has data for 59 of 3,029 — leading with the
                panel that is usually empty made the page read as broken.
                (Thumbnails used to hold this slot: a grid of near-identical
                stills above everything else.) */}
            <PageScorePanel video={video} brandId={brand.id} onCount={onPages} nested={nested} reloadKey={pagesNonce} />

            {/* Performance second, its own section. The two answer different
                questions about different objects — "is the page it sits on any
                good" and "did anyone watch this" — and only the second is
                governed by the date range and source filters, which is why they
                live in Performance's own header rather than above both. */}
            <PerformancePanel video={video} brandId={brand.id} />

            {/* Three panels share one row, so a thin video costs one screen
                instead of three consecutive empty sections. auto-fit gives
                3-up / 2-up / stacked without hardcoded breakpoints, which
                also survives the rail collapsing. */}
            <Box
                className="grid gap-5 items-stretch"
                sx={{gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))"}}
            >
                <StoriesPanel videoId={video.id} reloadKey={contentNonce} onCount={onStories} />
                <ThumbnailsPanel video={video} project={videoProject} onCount={onThumbnails} />
                <CaptionsPanel video={video} project={videoProject} onCount={onTranscripts} />
            </Box>
        </>
    );

    if (nested) {
        return (
            <MainContainer containerClassName={containerClassName}>
                <Box
                    className="border border-border-token rounded-xl overflow-hidden bg-surface"
                    sx={{boxShadow: "var(--sh-1)"}}
                >
                    <NestedModuleBar video={video} project={videoProject} onClose={onClose} menuProps={menuProps} />
                    <Box className="flex flex-col gap-4 p-4 bg-page">{body}</Box>
                </Box>
            </MainContainer>
        );
    }

    return (
        <MainContainer containerClassName={containerClassName}>
            <Box className="flex flex-col gap-5">{body}</Box>
        </MainContainer>
    );
}
